import { Project, DatabaseProject, WorkStatus, MaterialLabor, DatabaseMaterial, DatabaseLabor } from '@/types/project';

export const mapDatabaseProjectToProject = (dbProject: DatabaseProject): Project => {
  try {
    console.log(`🔄 [MAPPER] Starting to map project: ${dbProject.title}`);
    console.log(`🔄 [MAPPER] Raw materials:`, dbProject.materials?.length || 0, dbProject.materials);
    console.log(`🔄 [MAPPER] Raw labor:`, dbProject.labors?.length || 0, dbProject.labors);
    
    // Get the current date for default values
    const now = new Date().toISOString();

    // Extract status remarks from description
    let statusRemarks = '';
    let description = dbProject.description || '';
    if (description?.startsWith('Status Remarks: ')) {
      statusRemarks = description.substring(16).trim();
      description = '';
    }

    // Ensure we have valid dates
    const startDate = dbProject.start_date || now;
    const endDate = dbProject.end_date || now;

    // Real computed progress is kept in actual_progress; fall back to raw progress
    const rawProgress = typeof dbProject.progress === 'number' ? dbProject.progress : 0;
    const actualProgress =
      typeof (dbProject as any).actual_progress === 'number'
        ? (dbProject as any).actual_progress
        : rawProgress;

    // Handle work status
    let workStatus: WorkStatus = 'Pending';
    if (dbProject.status) {
      // Map database status to WorkStatus
      const statusMap: Record<string, WorkStatus> = {
        'In Progress': 'In Progress',
        'Complete': 'Complete',
        'Pending': 'Pending',
        'Delayed': 'Delayed'
      };
      workStatus = statusMap[dbProject.status] || workStatus;
    } else {
      // Auto-detect status based on dates and progress
      const endDateObj = new Date(dbProject.end_date || now);
      const today = new Date();

      if (rawProgress >= 100) {
        workStatus = 'Complete';
      } else if (endDateObj < today) {
        workStatus = 'Delayed';
      } else {
        workStatus = rawProgress > 0 ? 'In Progress' : 'Pending';
      }
    }

    // Displayed progress is always 100% once a project is flagged Complete,
    // while the real value is preserved in `actualProgress`.
    const progress = workStatus === 'Complete' ? 100 : rawProgress;

    // Map materials to MaterialLabor type with proper validation
    const mappedMaterials: MaterialLabor[] = [];
    if (Array.isArray(dbProject.materials) && dbProject.materials.length > 0) {
      console.log(`🔄 [MAPPER] Processing ${dbProject.materials.length} materials`);
      
      dbProject.materials.forEach((material: DatabaseMaterial, index) => {
        if (!material) {
          console.warn('⚠️ [MAPPER] Null material at index:', index);
          return;
        }
        
        console.log(`🔄 [MAPPER] Processing material ${index + 1}:`, {
          id: material.id,
          name: material.name || '',
          quantity: material.quantity || 0,
          unit: material.unit || '',
          valuePercentage: typeof material.value_percentage === 'number' ? material.value_percentage : 0
        });
        
        if (!material.id) {
          console.warn('⚠️ [MAPPER] Material missing ID:', material);
          return;
        }
        
        mappedMaterials.push({
          id: String(material.id),
          name: material.name || 'Unnamed Material',
          quantity: Number(material.quantity) || 0,
          unit: material.unit || 'pcs',
          unitPrice: 0,
          valuePercentage: typeof material.value_percentage === 'number' ? material.value_percentage : 0,
          startDate: material.start_date || startDate,
          endDate: material.end_date || endDate,
          remark: (material as any).remark || undefined,
          changeName: (material as any).change_name || undefined,
          changeQuantity: (material as any).change_quantity != null ? Number((material as any).change_quantity) : undefined,
          changeUnit: (material as any).change_unit || undefined,
          changeRemark: (material as any).change_remark || undefined,
        });
      });
    }
    
    console.log(`✅ [MAPPER] Final mapped materials:`, mappedMaterials.length, mappedMaterials);
    
    // Map labor to MaterialLabor type with proper validation
    const mappedLabor: MaterialLabor[] = [];
    if (Array.isArray(dbProject.labors) && dbProject.labors.length > 0) {
      console.log(`🔄 [MAPPER] Processing ${dbProject.labors.length} labor items`);
      
      dbProject.labors.forEach((labor: DatabaseLabor, index) => {
        if (!labor) {
          console.warn('⚠️ [MAPPER] Null labor at index:', index);
          return;
        }
        
        console.log(`🔄 [MAPPER] Processing labor ${index + 1}:`, {
          id: labor.id,
          name: labor.name || '',
          quantity: labor.quantity || 0,
          unit: labor.unit || '',
          valuePercentage: typeof labor.value_percentage === 'number' ? labor.value_percentage : 0
        });
        
        if (!labor.id) {
          console.warn('⚠️ [MAPPER] Labor missing ID:', labor);
          return;
        }
        
        mappedLabor.push({
          id: String(labor.id),
          name: labor.name || 'Unnamed Labor',
          quantity: Number(labor.quantity) || 0,
          unit: labor.unit || 'days',
          unitPrice: 0,
          valuePercentage: typeof labor.value_percentage === 'number' ? labor.value_percentage : 0,
          startDate: labor.start_date || startDate,
          endDate: labor.end_date || endDate,
          remark: (labor as any).remark || undefined,
          changeName: (labor as any).change_name || undefined,
          changeQuantity: (labor as any).change_quantity != null ? Number((labor as any).change_quantity) : undefined,
          changeUnit: (labor as any).change_unit || undefined,
          changeRemark: (labor as any).change_remark || undefined,
        });
      });
    }
    
    console.log(`✅ [MAPPER] Final mapped labor:`, mappedLabor.length, mappedLabor);
    
    // Create the final mapped project
    const mappedProject: Project = {
      id: dbProject.id,
      name: dbProject.title || '',
      projectNumber: dbProject.project_number || '',
      pic: dbProject.pic || '',
      supervisor: dbProject.supervisor || '',
      workStatus,
      status: workStatus,
      statusRemarks,
      progress,
      actualProgress,
      startDate,
      endDate,
      materials: mappedMaterials,
      labor: mappedLabor,
      createdAt: new Date(dbProject.created_at || now),
      updatedAt: new Date(dbProject.updated_at || now),
      description,
      location: dbProject.location || '',
      projectType: dbProject.project_type || '',
      budgetRAB: dbProject.budget_rab === null || dbProject.budget_rab === undefined
        ? undefined
        : Number(dbProject.budget_rab)
      ,
      globalBudget: dbProject.global_budget === null || dbProject.global_budget === undefined
        ? undefined
        : Number(dbProject.global_budget),
      globalActual: dbProject.global_actual === null || dbProject.global_actual === undefined
        ? undefined
        : Number(dbProject.global_actual)
    };

    console.log(`✅ [MAPPER] Final mapped project for ${mappedProject.name}:`, {
      id: mappedProject.id,
      name: mappedProject.name,
      materialsCount: mappedProject.materials.length,
      laborCount: mappedProject.labor.length,
      materials: mappedProject.materials,
      labor: mappedProject.labor
    });

    return mappedProject;
  } catch (error) {
    console.error('❌ [MAPPER] Error mapping project:', error);
    throw error;
  }
};

// Function to map a Project to DatabaseProject format
export const mapProjectToDatabase = (project: Project): Partial<DatabaseProject> => {
  try {
    // Map local workStatus to database status format
    const statusMap: Record<WorkStatus, string> = {
      'In Progress': 'In Progress',
      'Complete': 'Complete',
      'Pending': 'Pending',
      'Delayed': 'Delayed'
    };
    const status = statusMap[project.workStatus] || 'Pending';
    
    return {
      id: project.id,
      title: project.name,
      project_number: project.projectNumber,
      pic: project.pic,
      supervisor: project.supervisor,
      status,
      progress: project.progress,
      start_date: project.startDate,
      end_date: project.endDate,
      materials: project.materials.map((material: MaterialLabor) => ({
        id: material.id,
        name: material.name,
        quantity: material.quantity,
        unit: material.unit,
        value_percentage: material.valuePercentage,
        start_date: material.startDate,
        end_date: material.endDate
      })),
      labors: project.labor.map((labor: MaterialLabor) => ({
        id: labor.id,
        name: labor.name,
        quantity: labor.quantity,
        unit: labor.unit,
        value_percentage: labor.valuePercentage,
        start_date: labor.startDate,
        end_date: labor.endDate
      })),
      created_at: project.createdAt?.toISOString() || null,
      updated_at: project.updatedAt?.toISOString() || null,
      description: project.description,
      location: project.location,
      project_type: project.projectType,
      budget_rab: project.budgetRAB,
      global_budget: project.globalBudget,
      global_actual: project.globalActual
    };
  } catch (error) {
    console.error('Error mapping project to database:', error);
    throw error;
  }
};
